import React, { useState, useMemo } from 'react';
import { FiX, FiUsers, FiSearch } from 'react-icons/fi';
import { User, CreateUserGroupRequest } from '../../../shared/types/user';
import './CreateUserGroupModal.css';

interface CreateUserGroupModalProps {
  users: User[];
  onSave: (data: CreateUserGroupRequest) => void;
  onCancel: () => void;
  loading?: boolean;
}

const CreateUserGroupModal: React.FC<CreateUserGroupModalProps> = ({
  users,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    memberIds: [] as string[],
    permissions: [] as string[]
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const availablePermissions = [
    { id: 'view_drawings', label: 'View Drawings', description: 'View and access drawings' },
    { id: 'create_zones', label: 'Create Zones', description: 'Create new zones on drawings' },
    { id: 'edit_zones', label: 'Edit Zones', description: 'Edit existing zones' },
    { id: 'delete_zones', label: 'Delete Zones', description: 'Delete zones' },
    { id: 'view_reports', label: 'View Reports', description: 'Access reports and analytics' },
    { id: 'manage_users', label: 'Manage Users', description: 'Add and remove users' },
    { id: 'manage_groups', label: 'Manage Groups', description: 'Create and manage user groups' },
    { id: 'admin_access', label: 'Admin Access', description: 'Full administrative access' }
  ];

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(user => 
      user.firstName.toLowerCase().includes(term) ||
      user.lastName.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      user.department?.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleUserToggle = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      memberIds: prev.memberIds.includes(userId)
        ? prev.memberIds.filter(id => id !== userId)
        : [...prev.memberIds, userId]
    }));
  };

  const handlePermissionToggle = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    }

    if (formData.memberIds.length === 0) {
      newErrors.members = 'At least one member must be selected';
    }

    if (formData.permissions.length === 0) {
      newErrors.permissions = 'At least one permission must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSave(formData);
  };

  const selectedUsers = users.filter(user => formData.memberIds.includes(user.id));

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <FiUsers size={20} />
            <h2>Create User Group</h2>
          </div>
          <button
            className="modal-close-btn"
            onClick={onCancel}
            disabled={loading}
          >
            <FiX size={20} />
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Group Details</h3>
            
            <div className="form-group">
              <label className="form-label required">
                Group Name
              </label>
              <input
                type="text"
                className={`form-input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter group name"
                disabled={loading}
              />
              {errors.name && (
                <span className="error-message">{errors.name}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Description
              </label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter group description (optional)"
                rows={3}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Members</h3>
            <div className="form-group">
              <div className="search-box">
                <FiSearch size={16} />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={loading}
                />
              </div>
              
              {errors.members && (
                <span className="error-message">{errors.members}</span>
              )}

              <div className="members-list">
                {filteredUsers.map(user => (
                  <div
                    key={user.id}
                    className={`member-item ${formData.memberIds.includes(user.id) ? 'selected' : ''}`}
                    onClick={() => handleUserToggle(user.id)}
                  >
                    <div className="member-avatar">
                      {user.firstName[0]}{user.lastName[0]}
                    </div>
                    <div className="member-info">
                      <div className="member-name">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="member-details">
                        {user.email} • {user.department || 'No department'}
                      </div>
                    </div>
                    <div className="member-role">
                      {user.role}
                    </div>
                  </div>
                ))}
              </div>

              {selectedUsers.length > 0 && (
                <div className="selected-members">
                  <h4>Selected Members ({selectedUsers.length})</h4>
                  <div className="selected-members-list">
                    {selectedUsers.map(user => (
                      <div key={user.id} className="selected-member">
                        <div className="selected-member-avatar">
                          {user.firstName[0]}{user.lastName[0]}
                        </div>
                        <span className="selected-member-name">
                          {user.firstName} {user.lastName}
                        </span>
                        <button
                          type="button"
                          className="remove-member-btn"
                          onClick={() => handleUserToggle(user.id)}
                          disabled={loading}
                        >
                          <FiX size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Permissions</h3>
            <div className="form-group">
              {errors.permissions && (
                <span className="error-message">{errors.permissions}</span>
              )}
              
              <div className="permissions-grid">
                {availablePermissions.map(permission => (
                  <div
                    key={permission.id}
                    className={`permission-item ${formData.permissions.includes(permission.id) ? 'selected' : ''}`}
                    onClick={() => handlePermissionToggle(permission.id)}
                  >
                    <div className="permission-checkbox">
                      {formData.permissions.includes(permission.id) && (
                        <div className="checkbox-check">✓</div>
                      )}
                    </div>
                    <div className="permission-info">
                      <div className="permission-name">{permission.label}</div>
                      <div className="permission-description">{permission.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserGroupModal;
